import { Col, InputNumber, Row, Select, Tag } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { OverridableField } from 'features/libra/components/OverridableField'
import {
  calculateRequiredCoverage,
  getCalculatedZoneArea,
  getCofModifier,
  getEffectiveZoneArea,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { useZonePropertyAnchor } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import { useZoneUnitConversion } from 'features/libra/stepComponents/zoneAnalysis/useZoneUnitConversion'
import {
  GEOMETRY_OPTIONS,
  getEffectiveZoneHeight,
  MATERIAL_OPTIONS,
  ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import { SURFACE_AREA_FIELD } from 'features/libra/stepComponents/zoneAnalysis/zoneOverrides'
import {
  affectsZoneSurfaceArea,
  calculateZoneSurfaceAreaFromZone,
  DEFAULT_CYLINDER_PROPORTION,
  DEFAULT_HEAD_TYPE,
  DEFAULT_LENGTH_BASIS,
  deriveZoneSurfaceArea,
  HEAD_TYPE_OPTIONS,
  LENGTH_BASIS_OPTIONS,
} from 'features/libra/stepComponents/zoneAnalysis/zoneSurfaceArea'
import {
  canonicalizeMaterial,
  isMissingPrefillValue,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { useGeckoLocale } from 'features/locale'
import { RotateCcw } from 'lucide-react'
import { FC, useCallback } from 'react'
import styled, { useTheme } from 'styled-components'
import { GeckoMeasurementUnit } from 'types'

import { AiPrefillFieldState } from './AiPrefillFieldState'

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HelpText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin: 0 0 16px 0;
`

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin: 16px 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

/** Sub-field note: the calculated value, or how to fill the field. */
const HintText = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

export interface DesignFunctionPanelProps {
  zone: ZoneParams
  onChange: (updates: Partial<ZoneParams>) => void
  readonly?: boolean
}

/**
 * Plain numeric zone fields: each one's stored unit (the suffix its name
 * carries) and whether it can go below zero.
 */
const NUMBER_FIELDS = {
  external_diameter_mm: { stored: GeckoMeasurementUnit.Millimeters },
  wall_thickness_shell_mm: { stored: GeckoMeasurementUnit.Millimeters },
  wall_thickness_heads_mm: { stored: GeckoMeasurementUnit.Millimeters },
  corrosion_allowance_mm: { stored: GeckoMeasurementUnit.Millimeters },
  design_pressure_bar: { stored: GeckoMeasurementUnit.Bar },
  operating_pressure_bar: { stored: GeckoMeasurementUnit.Bar },
  design_temp_c: { stored: GeckoMeasurementUnit.Celsius, signed: true },
  max_operating_temp_c: { stored: GeckoMeasurementUnit.Celsius, signed: true },
} satisfies Record<string, { stored: GeckoMeasurementUnit; signed?: boolean }>

type SimpleNumberFieldKey = keyof typeof NUMBER_FIELDS

/**
 * A plain numeric zone field (stored in its fixed unit, shown in the
 * measurement toggle's preferred unit) where the comment anchor, AI provenance
 * chip, missing-value check, and input are all keyed off the one field id.
 * Fields with override/calculated behavior (height, surface area) stay bespoke.
 */
const ZoneNumberField: FC<
  DesignFunctionPanelProps & {
    field: SimpleNumberFieldKey
    label: string
    placeholder: string
    step: number
    anchor: ReturnType<typeof useZonePropertyAnchor>
  }
> = ({ field, label, placeholder, step, anchor, zone, onChange, readonly }) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const { toDisplay, toStored } = useZoneUnitConversion()
  const spec: { stored: GeckoMeasurementUnit; signed?: boolean } =
    NUMBER_FIELDS[field]
  const displayUnit = getPreferredMeasurementUnit(spec.stored)

  return (
    <CommentIndicator anchor={anchor(field)}>
      <FieldGroup>
        <Label>
          {label} ({displayUnit.name})
          <AiPrefillFieldState
            meta={zone.ai_prefill?.design_function}
            field={field}
            empty={isMissingPrefillValue(field, zone[field])}
          />
        </Label>
        <InputNumber
          value={toDisplay(zone[field], spec.stored)}
          onChange={(value) => {
            const updates: Partial<ZoneParams> = {}
            updates[field] = toStored(value, spec.stored)
            onChange(updates)
          }}
          placeholder={placeholder}
          style={{ width: '100%' }}
          min={spec.signed ? undefined : 0}
          step={step}
          disabled={readonly}
        />
      </FieldGroup>
    </CommentIndicator>
  )
}

export const DesignFunctionPanel: FC<DesignFunctionPanelProps> = ({
  zone,
  onChange: onChangeProp,
  readonly,
}) => {
  const anchor = useZonePropertyAnchor(zone.id)
  const theme = useTheme()
  const { confidence, cof } = useZoneAnalysisData()

  /**
   * Geometry edits carry their consequences with them. An edit to a
   * surface-area input recomputes the area, and because that area selects
   * F_ZONE, it recomputes coverage too — otherwise the stored
   * `coverage_calculation` silently disagrees with the area on screen. Mirrors
   * `RbaInformationPanel.handleFieldChange`, which does the same for its own
   * modifiers, and `applyTabFragmentWithDerivations` on the AI path.
   */
  const onChange = useCallback(
    (updates: Partial<ZoneParams>) => {
      if (!affectsZoneSurfaceArea(updates)) {
        onChangeProp(updates)
        return
      }

      const edited = { ...zone, ...updates }
      const area = deriveZoneSurfaceArea(edited)
      const coverage = calculateRequiredCoverage(
        { ...edited, ...area },
        getCofModifier(cof.level),
        confidence
      )

      onChangeProp({
        ...updates,
        ...area,
        // Undefined means no strategy is selected yet, so there is nothing
        // valid to store — leave whatever is there rather than clearing it.
        ...(coverage ? { coverage_calculation: coverage } : {}),
      })
    },
    [onChangeProp, zone, cof.level, confidence]
  )

  const breakdown = calculateZoneSurfaceAreaFromZone(zone)
  const hasAreaOverride = zone.total_surface_area_sq_m_override !== undefined

  // Zone values are stored in fixed metric units (m² is the workbook's area
  // unit); the fields display and edit them in whichever unit the global
  // measurement toggle prefers.
  const { getPreferredMeasurementUnit, formatNumber } = useGeckoLocale()
  const areaUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.SquareMeters
  )
  const lengthUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.Millimeters
  )
  const { toDisplay, toStored } = useZoneUnitConversion()
  const formatArea = (areaSqM: number): string =>
    formatNumber({
      input: areaSqM,
      inputUnit: GeckoMeasurementUnit.SquareMeters,
      maximumFractionDigits: 2,
    })

  return (
    <Panel>
      <HelpText>
        Define the physical and design parameters for this zone. These
        parameters inform corrosion risk assessment, inspection technique
        selection, and deployment methods.
      </HelpText>

      <SectionTitle>Material & Geometry</SectionTitle>

      <Row gutter={16}>
        <Col span={12}>
          <CommentIndicator anchor={anchor('material')}>
            <FieldGroup>
              <Label>
                Zone Material
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='material'
                  empty={isMissingPrefillValue('material', zone.material)}
                />
              </Label>
              <Select
                value={
                  zone.material
                    ? canonicalizeMaterial(zone.material)
                    : undefined
                }
                onChange={(value) => onChange({ material: value })}
                placeholder='Select material'
                options={[...MATERIAL_OPTIONS]}
                allowClear
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={12}>
          <CommentIndicator anchor={anchor('geometry')}>
            <FieldGroup>
              <Label>
                Zone Geometry
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='geometry'
                  empty={isMissingPrefillValue('geometry', zone.geometry)}
                />
              </Label>
              <Select
                value={zone.geometry}
                onChange={(value) => onChange({ geometry: value })}
                placeholder='Select geometry'
                options={[...GEOMETRY_OPTIONS]}
                allowClear
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <SectionTitle>Design & Operating Conditions</SectionTitle>

      <Row gutter={16}>
        <Col span={12}>
          <ZoneNumberField
            field='design_pressure_bar'
            label='Design Pressure'
            placeholder='e.g., 15.0'
            step={0.1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
        <Col span={12}>
          <ZoneNumberField
            field='operating_pressure_bar'
            label='Operating Pressure'
            placeholder='e.g., 12.5'
            step={0.1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <ZoneNumberField
            field='design_temp_c'
            label='Design Temperature'
            placeholder='e.g., 60'
            step={1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
        <Col span={12}>
          <ZoneNumberField
            field='max_operating_temp_c'
            label='Max Operating Temperature'
            placeholder='e.g., 45'
            step={1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
      </Row>

      <SectionTitle>Zone Dimensions</SectionTitle>

      <Row gutter={16}>
        <Col span={12}>
          <CommentIndicator anchor={anchor('height_mm')}>
            <FieldGroup>
              <Label>
                Zone Height or Length ({lengthUnit.name})
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='height_mm'
                  empty={!getEffectiveZoneHeight(zone)}
                />
                {!readonly && zone.height_mm_override && (
                  <Tag
                    color='orange'
                    style={{ marginLeft: 8, cursor: 'pointer' }}
                    icon={<RotateCcw size={12} />}
                    onClick={() => onChange({ height_mm_override: undefined })}>
                    Reset
                  </Tag>
                )}
              </Label>
              <InputNumber
                value={toDisplay(
                  getEffectiveZoneHeight(zone),
                  GeckoMeasurementUnit.Millimeters
                )}
                onChange={(value) => {
                  // Typing the calculated value back is not an override;
                  // compared in display units, where the SME types.
                  const calculatedDisplay = toDisplay(
                    zone.calculated_height_mm,
                    GeckoMeasurementUnit.Millimeters
                  )
                  const override =
                    value !== calculatedDisplay
                      ? toStored(value, GeckoMeasurementUnit.Millimeters)
                      : undefined
                  onChange({ height_mm_override: override })
                }}
                placeholder='Enter height or length'
                style={{ width: '100%' }}
                min={0}
                disabled={readonly}
              />
              {zone.calculated_height_mm && (
                <HintText>
                  Calculated:{' '}
                  {formatNumber({
                    input: zone.calculated_height_mm,
                    inputUnit: GeckoMeasurementUnit.Millimeters,
                    maximumFractionDigits: 2,
                  })}
                  {zone.height_mm_override && ' (Overridden)'}
                </HintText>
              )}
              {!zone.calculated_height_mm &&
                Boolean(zone.drawing_bounds?.length) && (
                  <HintText>
                    Document crop bounds have no physical scale; enter this
                    value manually.
                  </HintText>
                )}
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={12}>
          <ZoneNumberField
            field='external_diameter_mm'
            label='Zone Outer Diameter (OD)'
            placeholder='Enter outer diameter'
            step={1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
      </Row>

      <SectionTitle>Surface Area</SectionTitle>

      <HelpText>
        Computed from the outer diameter and length above, matching the NII
        master workbook&apos;s Surface Area Calculator. Heads default to none —
        set the head equivalents for a zone that includes a dished end.
      </HelpText>

      <Row gutter={16}>
        <Col span={12}>
          <CommentIndicator anchor={anchor('length_basis')}>
            <FieldGroup>
              <Label>
                Length Basis
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='length_basis'
                  empty={isMissingPrefillValue(
                    'length_basis',
                    zone.length_basis
                  )}
                />
              </Label>
              <Select
                value={zone.length_basis ?? DEFAULT_LENGTH_BASIS}
                onChange={(value) => onChange({ length_basis: value })}
                options={[...LENGTH_BASIS_OPTIONS]}
                disabled={readonly}
              />
              <HintText>
                Which length the drawing gave. Seam to seam stops short of the
                tangent line, so a straight flange is added per head.
              </HintText>
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={12}>
          <CommentIndicator anchor={anchor('head_type')}>
            <FieldGroup>
              <Label>
                Head Type
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='head_type'
                  empty={isMissingPrefillValue('head_type', zone.head_type)}
                />
              </Label>
              <Select
                value={zone.head_type ?? DEFAULT_HEAD_TYPE}
                onChange={(value) => onChange({ head_type: value })}
                options={[...HEAD_TYPE_OPTIONS]}
                disabled={readonly}
              />
              <HintText>Ellipsoidal in the large majority of vessels.</HintText>
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <CommentIndicator anchor={anchor('cylinder_proportion')}>
            <FieldGroup>
              <Label>
                Proportion of Cylinder
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='cylinder_proportion'
                  empty={isMissingPrefillValue(
                    'cylinder_proportion',
                    zone.cylinder_proportion
                  )}
                />
              </Label>
              <InputNumber
                value={zone.cylinder_proportion ?? DEFAULT_CYLINDER_PROPORTION}
                onChange={(value) =>
                  onChange({ cylinder_proportion: value ?? undefined })
                }
                style={{ width: '100%' }}
                min={0}
                max={1}
                step={0.01}
                disabled={readonly}
              />
              <HintText>
                Leave at 1 when the length above is the zone&apos;s own length;
                use the fraction when it is the whole vessel.
              </HintText>
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={12}>
          <CommentIndicator anchor={anchor('head_proportion')}>
            <FieldGroup>
              <Label>
                Head Equivalents
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='head_proportion'
                  empty={isMissingPrefillValue(
                    'head_proportion',
                    zone.head_proportion
                  )}
                />
              </Label>
              <InputNumber
                value={zone.head_proportion ?? 0}
                onChange={(value) =>
                  onChange({ head_proportion: value ?? undefined })
                }
                style={{ width: '100%' }}
                min={0}
                max={2}
                step={0.5}
                disabled={readonly}
              />
              <HintText>
                Whole heads in this zone: both ends 2, one end 1, half a head
                0.5.
              </HintText>
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <CommentIndicator anchor={anchor('total_surface_area_sq_m')}>
            <FieldGroup>
              <Label>
                Zone Total Surface Area ({areaUnit.name})
                <AiPrefillFieldState
                  meta={zone.ai_prefill?.design_function}
                  field='total_surface_area_sq_m'
                  empty={getEffectiveZoneArea(zone) <= 0}
                />
              </Label>
              <OverridableField
                label={SURFACE_AREA_FIELD.label}
                calculatedValue={getCalculatedZoneArea(zone)}
                overrideValue={zone.total_surface_area_sq_m_override}
                justification={
                  zone.total_surface_area_sq_m_override_justification
                }
                formatValue={formatArea}
                isSameValue={(a, b) =>
                  toDisplay(a, GeckoMeasurementUnit.SquareMeters) ===
                  toDisplay(b, GeckoMeasurementUnit.SquareMeters)
                }
                renderEditor={(value, setValue) => (
                  <InputNumber
                    value={toDisplay(value, GeckoMeasurementUnit.SquareMeters)}
                    onChange={(display) =>
                      setValue(
                        toStored(display, GeckoMeasurementUnit.SquareMeters)
                      )
                    }
                    placeholder='Enter total surface area'
                    suffix={areaUnit.name}
                    min={0}
                    step={0.01}
                  />
                )}
                onChange={(value, justification) =>
                  onChange({
                    total_surface_area_sq_m_override: value,
                    total_surface_area_sq_m_override_justification:
                      justification,
                  })
                }
                readonly={readonly}
              />
              {breakdown ? (
                <HintText>
                  Cylinder {formatArea(breakdown.cylinderContributionSqM)} +
                  heads {formatArea(breakdown.headContributionSqM)} ={' '}
                  {formatArea(breakdown.totalSqM)}
                  {hasAreaOverride && ' (overridden above)'}
                </HintText>
              ) : (
                <HintText>
                  Enter an outer diameter to calculate the area, or type it in
                  directly.
                </HintText>
              )}
              {zone.calculated_area_sq_m !== undefined && (
                <HintText>
                  Asset model bounds: {formatArea(zone.calculated_area_sq_m)}
                </HintText>
              )}
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <SectionTitle>Zone Wall Thickness & Corrosion Allowance</SectionTitle>

      <Row gutter={16}>
        <Col span={8}>
          <ZoneNumberField
            field='wall_thickness_shell_mm'
            label='Zone Wall Thickness - Shell'
            placeholder='Zone Shell thickness'
            step={0.1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
        <Col span={8}>
          <ZoneNumberField
            field='wall_thickness_heads_mm'
            label='Zone Wall Thickness - Heads'
            placeholder='Zone Head thickness'
            step={0.1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
        <Col span={8}>
          <ZoneNumberField
            field='corrosion_allowance_mm'
            label='Zone Corrosion Allowance'
            placeholder='Zone CA'
            step={0.1}
            anchor={anchor}
            zone={zone}
            onChange={onChange}
            readonly={readonly}
          />
        </Col>
      </Row>

      <div
        style={{
          marginTop: 16,
          padding: 12,
          background: theme.forecast.blueGlassBackground,
          borderRadius: 6,
          fontSize: 12,
          color: theme.forecast.secondaryText,
        }}>
        <strong>Informs:</strong> These parameters affect Corrosion Risk
        Assessment, Inspection Technique Selection, Deployment Method Selection,
        and Coverage Calculation.
      </div>
    </Panel>
  )
}
